import { NavLink } from 'react-router';
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCaregiverPortal } from '../context/CaregiverPortalContext';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
}

const listItemStyles = {
  color: 'white',
  '& .MuiListItemText-primary': {
    color: 'white',
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    cursor: 'pointer',
    '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
      color: '#8900e1',
    },
  },
};

export default function NavDrawer({ open, onClose }: NavDrawerProps) {
  const { hasRole, logout } = useAuth();
  const { formSubmitted } = useCaregiverPortal();
  const { t } = useTranslation();

  const handleLogout = () => {
    onClose();
    logout();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: '#330662',
          color: 'white',
        },
      }}
    >
      <Box
        sx={{ width: 250 }}
        role="presentation"
        onClick={onClose}
        onKeyDown={onClose}
      >
        <List>
          {hasRole('caregiver') && formSubmitted && (
            <ListItemButton component={NavLink} to="/caregivers" end sx={listItemStyles}>
              <ListItemIcon>
                <HomeIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('nav.home', 'Home')} />
            </ListItemButton>
          )}

          {hasRole('caregiver') && (
            <ListItemButton component={NavLink} to="/caregiverform" sx={listItemStyles}>
              <ListItemIcon>
                <AppRegistrationIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('nav.caregiverForm', 'Caregiver Form')} />
            </ListItemButton>
          )}

          {/* Only once the caregiver form is submitted; applicants mid-form never see it */}
          {hasRole('caregiver') && formSubmitted && (
            <ListItemButton component={NavLink} to="/availability" sx={listItemStyles}>
              <ListItemIcon>
                <EventAvailableIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary={t('nav.availability', 'Update Availability')} />
            </ListItemButton>
          )}

          {(hasRole('marketer') || hasRole('recruiter')) && (
            <ListItemButton component={NavLink} to="/marketer-entry" sx={listItemStyles}>
              <ListItemIcon>
                <AssignmentIndIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary="Lead Entry" />
            </ListItemButton>
          )}

          {hasRole('admin') && (
            <ListItemButton component={NavLink} to="/templates" sx={listItemStyles}>
              <ListItemIcon>
                <DescriptionIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary="Templates" />
            </ListItemButton>
          )}

          {hasRole('admin') && (
            <ListItemButton component={NavLink} to="/documents" sx={listItemStyles}>
              <ListItemIcon>
                <FolderIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary="Documents" />
            </ListItemButton>
          )}

          {(hasRole('quick-entry') || hasRole('admin')) && (
            <>
              <ListItemButton component={NavLink} to="/qe-landing" sx={listItemStyles}>
                <ListItemIcon><PeopleIcon sx={{ color: 'white' }} /></ListItemIcon>
                <ListItemText primary="QE Leads" />
              </ListItemButton>
              <ListItemButton component={NavLink} to="/quick-entry" sx={listItemStyles}>
                <ListItemIcon><PersonAddIcon sx={{ color: 'white' }} /></ListItemIcon>
                <ListItemText primary="QE New Lead" />
              </ListItemButton>
            </>
          )}

          {hasRole('admin') && (
            <ListItemButton component={NavLink} to="/admin/settings" sx={listItemStyles}>
              <ListItemIcon>
                <SettingsIcon sx={{ color: 'white' }} />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          )}

          <ListItemButton onClick={handleLogout} sx={listItemStyles}>
            <ListItemIcon>
              <LogoutIcon sx={{ color: 'white' }} />
            </ListItemIcon>
            <ListItemText primary={t('nav.logout', 'Log Out')} />
          </ListItemButton>
        </List>
      </Box>
    </Drawer>
  );
}
